import { Suspense, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'react-hot-toast';

// Auth
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Signup from './pages/Signup';

// Layout
import { MainLayout } from '@/components/layout/MainLayout';

// Pages
import { HomePage } from '@/pages/HomePage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { ActionsPage } from '@/pages/ActionsPage';
import { WeeklyUpdatePage } from '@/pages/WeeklyUpdatePage';
import { DashboardPage } from '@/pages/DashboardPage';
import { VideoLibraryPage } from '@/pages/VideoLibraryPage';
import { VideoPlayerPage } from '@/pages/VideoPlayerPage';
import { ProgramUpdatesPage } from '@/pages/ProgramUpdatesPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoadingPage } from '@/pages/LoadingPage';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { TeamsPage } from '@/pages/TeamsPage';
import { TeamMembersPage } from '@/pages/TeamMembersPage';

// Components
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorFallback } from '@/components/common/ErrorFallback';
import { BackgroundWrapper } from '@/components/layout/BackgroundWrapper';

const App = () => {
  const [isLoading, setIsLoading] = useState(() => {
    // Check if video has already been shown in this session
    return !sessionStorage.getItem('videoShown');
  });

  const handleLoadingComplete = () => {
    sessionStorage.setItem('videoShown', 'true');
    setIsLoading(false);
  };

  if (isLoading) {
    return <LoadingPage onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <AuthProvider>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '12px',
            padding: '16px',
          },
        }}
      />
      <AppContainer>
        <BackgroundWrapper />
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <MainLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/actions" element={<ActionsPage />} />
                <Route path="/weekly-update" element={<WeeklyUpdatePage />} />
                <Route path="/video-library" element={<VideoLibraryPage />} />
                <Route path="/video-player/:videoId" element={<VideoPlayerPage />} />
                <Route path="/program-updates" element={<ProgramUpdatesPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/grip-majlis" element={<ComingSoonPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/team-members" element={<TeamMembersPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AppContainer>
    </AuthProvider>
  );
};

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
`;

export default App;
